import * as z from "zod";

/*
 * Synced entities. The same tables exist in D1 (source of truth) and in every
 * client's SQLite replica. Every row carries:
 *   seq     – server-assigned global change counter (USN). 0 = never synced.
 *   deleted – tombstone. The row stays (with content cleared) so deletes sync.
 * Timestamps are epoch milliseconds.
 */

const id = z.string().min(1).max(64);
const ms = z.number().int();
const nullableMs = ms.nullable();

function isIdArray(s: string): boolean {
  try {
    const v: unknown = JSON.parse(s);
    return Array.isArray(v) && v.every((x) => typeof x === "string" && x.length > 0 && x.length <= 64);
  } catch {
    return false;
  }
}

export const notebookSchema = z.object({
  id,
  name: z.string().max(200),
  parent_id: id.nullable(), // stack id; null = top level
  kind: z.enum(["stack", "notebook"]),
  created: ms,
  updated: ms,
});

export const noteSchema = z.object({
  id,
  notebook_id: id,
  title: z.string().max(1000),
  body_html: z.string(),
  body_text: z.string(),
  tag_ids: z.string().max(20000).refine(isIdArray, "tag_ids must be a JSON array of ids"), // JSON array of tag ids
  created: ms,
  updated: ms,
  trashed_at: nullableMs,
  source_url: z.string().max(4000).nullable(),
  pinned_at: nullableMs, // "Pin to notebook"
  locked_at: nullableMs.optional(), // "Lock note" (edit protection); optional so older clients' pushes stay valid
  version: z.number().int(), // bumped on every body/title change; conflict detection
});

export const tagSchema = z.object({
  id,
  name: z.string().max(100),
  parent_id: id.nullable(),
  created: ms,
  updated: ms,
});

export const taskSchema = z.object({
  id,
  note_id: id,
  title: z.string().max(4000),
  completed_at: nullableMs,
  due_at: nullableMs,
  due_all_day: z.number().int().min(0).max(1), // due date without a time
  reminder_at: nullableMs,
  flagged: z.number().int().min(0).max(1),
  recurrence: z.string().max(500).nullable(), // RFC 5545 RRULE
  position: z.number(),
  created: ms,
  updated: ms,
});

export const attachmentSchema = z.object({
  id,
  note_id: id,
  sha256: z.string().regex(/^[0-9a-f]{64}$/),
  filename: z.string().max(500),
  mime: z.string().max(200),
  size: z.number().int().min(0),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  created: ms,
});

export const shortcutSchema = z.object({
  id,
  kind: z.enum(["note", "notebook", "stack", "tag", "search"]),
  target_id: id,
  position: z.number(),
  created: ms,
});

export const savedSearchSchema = z.object({
  id,
  name: z.string().max(200),
  query: z.string().max(4000),
  created: ms,
  updated: ms,
});

/** "Send feedback" from any device. The owner's repo pulls new items (tools/feedback/pull.mjs), Claude replies. */
export const feedbackSchema = z.object({
  id,
  created: ms,
  updated: ms,
  text: z.string().max(20000),
  /** JSON: page, note id, screen, device, app build, time zone. */
  context: z.string().max(4000),
  /** Optional screenshot/photo, stored in R2 by sha256 like attachments. */
  image_sha: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  image_mime: z.string().max(100).nullable(),
  status: z.enum(["new", "seen", "done"]),
  reply: z.string().max(4000),
  /** Claude's one-line rephrasing (tools/feedback/pull.mjs summary); the list shows it, the full text on expanding. */
  summary: z.string().max(300),
  /** A voice message sent with it: the recording in R2 by sha256, like the image. The server types it out into `text`. */
  audio_sha: z.string().regex(/^[0-9a-f]{64}$/).nullable(),
  audio_mime: z.string().max(100).nullable(),
  /** "" none; "pending" waiting to be typed out (server: apps/server/src/transcribe.ts); "done"; "failed". */
  voice: z.enum(["", "pending", "done", "failed"]),
});

export const settingSchema = z.object({
  id, // setting key, e.g. "home.layout", "defaultNotebook", "scratchPad"
  value: z.string(), // JSON
  updated: ms,
});

export const ENTITY_SCHEMAS = {
  notebooks: notebookSchema,
  notes: noteSchema,
  tags: tagSchema,
  tasks: taskSchema,
  attachments: attachmentSchema,
  shortcuts: shortcutSchema,
  saved_searches: savedSearchSchema,
  settings: settingSchema,
  feedback: feedbackSchema,
} as const;

export type EntityName = keyof typeof ENTITY_SCHEMAS;
export const ENTITY_NAMES = Object.keys(ENTITY_SCHEMAS) as EntityName[];

type Synced = { seq: number; deleted: 0 | 1 };
export type Notebook = z.infer<typeof notebookSchema> & Synced;
export type Note = z.infer<typeof noteSchema> & Synced;
export type Tag = z.infer<typeof tagSchema> & Synced;
export type Task = z.infer<typeof taskSchema> & Synced;
export type Attachment = z.infer<typeof attachmentSchema> & Synced;
export type Shortcut = z.infer<typeof shortcutSchema> & Synced;
export type SavedSearch = z.infer<typeof savedSearchSchema> & Synced;
export type Setting = z.infer<typeof settingSchema> & Synced;
export type Feedback = z.infer<typeof feedbackSchema> & Synced;

export interface EntityRow {
  notebooks: Notebook;
  notes: Note;
  tags: Tag;
  tasks: Task;
  attachments: Attachment;
  shortcuts: Shortcut;
  saved_searches: SavedSearch;
  settings: Setting;
  feedback: Feedback;
}

/** Writable columns (everything except id/seq/deleted) per entity. */
export function entityColumns(t: EntityName): string[] {
  return Object.keys(ENTITY_SCHEMAS[t].shape).filter((c) => c !== "id");
}

/**
 * Columns added after a table was first created. D1 gets them from a numbered migration; client
 * replicas made before then add them at startup (CREATE TABLE IF NOT EXISTS doesn't).
 */
export const ADDED_COLUMNS: [EntityName, string, string][] = [
  ["notes", "locked_at", "INTEGER"],
  ["feedback", "summary", "TEXT NOT NULL DEFAULT ''"],
  ["feedback", "audio_sha", "TEXT"],
  ["feedback", "audio_mime", "TEXT"],
  ["feedback", "voice", "TEXT NOT NULL DEFAULT ''"],
];

/**
 * DDL shared by D1 and the client replica. Indexes are deliberately few: D1
 * bills each index write as a row written.
 */
export const SYNCED_DDL = `
CREATE TABLE IF NOT EXISTS notebooks (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL DEFAULT '',
  parent_id TEXT,
  kind TEXT NOT NULL DEFAULT 'notebook',
  created INTEGER NOT NULL DEFAULT 0,
  updated INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS notebooks_seq ON notebooks(seq);

CREATE TABLE IF NOT EXISTS notes (
  id TEXT PRIMARY KEY,
  notebook_id TEXT NOT NULL DEFAULT '',
  title TEXT NOT NULL DEFAULT '',
  body_html TEXT NOT NULL DEFAULT '',
  body_text TEXT NOT NULL DEFAULT '',
  tag_ids TEXT NOT NULL DEFAULT '[]',
  created INTEGER NOT NULL DEFAULT 0,
  updated INTEGER NOT NULL DEFAULT 0,
  trashed_at INTEGER,
  source_url TEXT,
  pinned_at INTEGER,
  locked_at INTEGER,
  version INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS notes_seq ON notes(seq);

CREATE TABLE IF NOT EXISTS tags (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL DEFAULT '',
  parent_id TEXT,
  created INTEGER NOT NULL DEFAULT 0,
  updated INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS tags_seq ON tags(seq);

CREATE TABLE IF NOT EXISTS tasks (
  id TEXT PRIMARY KEY,
  note_id TEXT NOT NULL DEFAULT '',
  title TEXT NOT NULL DEFAULT '',
  completed_at INTEGER,
  due_at INTEGER,
  due_all_day INTEGER NOT NULL DEFAULT 0,
  reminder_at INTEGER,
  flagged INTEGER NOT NULL DEFAULT 0,
  recurrence TEXT,
  position REAL NOT NULL DEFAULT 0,
  created INTEGER NOT NULL DEFAULT 0,
  updated INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS tasks_seq ON tasks(seq);

CREATE TABLE IF NOT EXISTS attachments (
  id TEXT PRIMARY KEY,
  note_id TEXT NOT NULL DEFAULT '',
  sha256 TEXT NOT NULL DEFAULT '',
  filename TEXT NOT NULL DEFAULT '',
  mime TEXT NOT NULL DEFAULT 'application/octet-stream',
  size INTEGER NOT NULL DEFAULT 0,
  width INTEGER,
  height INTEGER,
  created INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS attachments_seq ON attachments(seq);

CREATE TABLE IF NOT EXISTS shortcuts (
  id TEXT PRIMARY KEY,
  kind TEXT NOT NULL DEFAULT 'note',
  target_id TEXT NOT NULL DEFAULT '',
  position REAL NOT NULL DEFAULT 0,
  created INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS shortcuts_seq ON shortcuts(seq);

CREATE TABLE IF NOT EXISTS saved_searches (
  id TEXT PRIMARY KEY,
  name TEXT NOT NULL DEFAULT '',
  query TEXT NOT NULL DEFAULT '',
  created INTEGER NOT NULL DEFAULT 0,
  updated INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS saved_searches_seq ON saved_searches(seq);

CREATE TABLE IF NOT EXISTS settings (
  id TEXT PRIMARY KEY,
  value TEXT NOT NULL DEFAULT 'null',
  updated INTEGER NOT NULL DEFAULT 0,
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS settings_seq ON settings(seq);

CREATE TABLE IF NOT EXISTS feedback (
  id TEXT PRIMARY KEY,
  created INTEGER NOT NULL DEFAULT 0,
  updated INTEGER NOT NULL DEFAULT 0,
  text TEXT NOT NULL DEFAULT '',
  context TEXT NOT NULL DEFAULT '{}',
  image_sha TEXT,
  image_mime TEXT,
  status TEXT NOT NULL DEFAULT 'new',
  reply TEXT NOT NULL DEFAULT '',
  summary TEXT NOT NULL DEFAULT '',
  audio_sha TEXT,
  audio_mime TEXT,
  voice TEXT NOT NULL DEFAULT '',
  seq INTEGER NOT NULL DEFAULT 0,
  deleted INTEGER NOT NULL DEFAULT 0
);
CREATE INDEX IF NOT EXISTS feedback_seq ON feedback(seq);
`;
